<template>
  <v-app>
    <v-main class="d-flex align-center justify-center login-bg">
      <v-container class="login-container" max-width="540">
        <v-card class="login-card" elevation="8" style="border-radius: 16px;">
          <v-card-text>
            <div class="text-center mb-4">
              <v-img :src="appLogo" contain max-width="190" class="mx-auto mb-3" />
              <h5 class="login-heading mb-5"> <span class="red--text font-weight-bold">Academic Service</span> Portal Login </h5>
            </div>
            <v-btn 
              block 
              class="mt-2 py-3"
              style="background: linear-gradient(135deg, #7b2d8e, #9b59b6);"
              height="auto"
              large 
              dark 
              @click="startMicrosoftLogin"
              :loading="msalLoading"
            >
              <div class="d-flex flex-column align-center" style="text-transform: none; letter-spacing: normal;">
                <span class="font-weight-bold text-subtitle-1">MUIC SSO (Preferred)</span>
                <small class="mt-1 font-weight-regular" style="font-size: 0.8rem;">Login with your SKY+/MUIC Account @muic.io</small>
              </div>
            </v-btn>

            <v-alert
              v-if="errorMessage"
              type="error"
              dense
              class="mt-4"
              dismissible
              @input="errorMessage = ''"
            >
              {{ errorMessage }}
            </v-alert>
           
            <div class="login-footer">
              2026 creativeLabs IC-DEV | Mahidol University International College.
            </div>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LoginView',
  data: () => ({
    msalLoading: false,
    errorMessage: '',
  }),
  computed: {
    appLogo() {
      return require('@/assets/MUIC_Logo_Eng_Center.png')
    },
  },
  mounted() {
    sessionStorage.removeItem('msal.interaction.status')
    
    // Check if whoami failed (from router guard)
    const whoamiFailed = localStorage.getItem('whoamiFailed')
    if (whoamiFailed) {
      const authTypeLabel = whoamiFailed === 'MSAL' ? 'Microsoft' : 'Keycloak'
      this.errorMessage = `ไม่สามารถยืนยันตัวตนกับระบบได้ (${authTypeLabel}) กรุณาติดต่อผู้ดูแลระบบ`
      localStorage.removeItem('whoamiFailed') // Remove flag after showing
    } else {
      this.errorMessage = ''
    }
  },
  methods: {
    // Clear all sessions (MSAL and Keycloak)
    clearAllAuth() {
      localStorage.clear()
      sessionStorage.clear()
    },

    // Verify whoami after login
    async verifyWithWhoami() {
      try {
        const res = await axios.get('/whoami/')
        if (res.status === 200 && res.data) {
          return true
        }
        return false
      } catch (error) {
        console.error('❌ whoami verification failed:', error?.response?.status || error.message)
        return false
      }
    },

    async startMicrosoftLogin() {
      this.errorMessage = ''
      
      const msal = this.$msal
      if (!msal) {
        console.error('MSAL instance not available')
        this.errorMessage = 'MSAL ไม่พร้อมใช้งาน'
        return
      }

      const interactionStatus = sessionStorage.getItem('msal.interaction.status')
      if (interactionStatus) {
        this.errorMessage = 'กำลังเข้าสู่ระบบอยู่ กรุณารอสักครู่'
        return
      }

      const accounts = msal.getAllAccounts()
      if (accounts.length > 0) {
        try {
          const response = await msal.acquireTokenSilent({
            account: accounts[0],
            scopes: ['User.Read', 'api://cd589853-e246-4124-a2cc-3b3bb299db78/instructor-prod'],
          })
          
          if (response) {
            console.log('🔐 Login: MSAL (silent token)')
            msal.setActiveAccount(accounts[0])
            localStorage.setItem('isAuthenticated', 'true')
            localStorage.setItem('isAuthenType', 'MSAL')
            localStorage.setItem('isToken', response.idToken || response.accessToken)
            
            // Verify whoami
            const whoamiOk = await this.verifyWithWhoami()
            if (!whoamiOk) {
              this.clearAllAuth()
              this.errorMessage = 'ไม่สามารถยืนยันตัวตนกับระบบได้ กรุณาติดต่อผู้ดูแลระบบ'
              return
            }
            
            this.$router.push('/')
            return
          }
        } catch (silentError) {
          // Silent login failed, using popup
        }
      }

      this.msalLoading = true
      
      const loginRequest = {
        scopes: ['User.Read', 'api://cd589853-e246-4124-a2cc-3b3bb299db78/instructor-prod'],
        prompt: 'select_account',
      }

      try {
        if (typeof msal.initialize === 'function' && !msal.__factoryInitialized) {
          await msal.initialize()
          msal.__factoryInitialized = true
        }
        
        const response = await msal.loginPopup(loginRequest)
        
        if (response && response.account) {
          console.log('🔐 Login: MSAL (popup) -', response.account.username)
          msal.setActiveAccount(response.account)
          localStorage.setItem('isAuthenticated', 'true')
          localStorage.setItem('isAuthenType', 'MSAL')
          localStorage.setItem('isToken', response.idToken || response.accessToken)
          
          // Verify whoami
          const whoamiOk = await this.verifyWithWhoami()
          if (!whoamiOk) {
            this.clearAllAuth()
            this.msalLoading = false
            this.errorMessage = 'ไม่สามารถยืนยันตัวตนกับระบบได้ กรุณาติดต่อผู้ดูแลระบบ'
            return
          }
          
          this.$router.push('/')
        } else {
          throw new Error('ไม่พบข้อมูล account หลัง login')
        }
        
      } catch (err) {
        console.error('MSAL login popup failed:', err)
        this.msalLoading = false
        sessionStorage.removeItem('msal.interaction.status')
        
        if (err.errorCode === 'user_cancelled') {
          this.errorMessage = 'คุณยกเลิกการเข้าสู่ระบบ'
        } else if (err.errorCode === 'popup_window_error') {
          this.errorMessage = 'ไม่สามารถเปิด popup ได้ กรุณาอนุญาต popup สำหรับเว็บไซต์นี้'
        } else if (err.errorCode === 'interaction_in_progress') {
          this.errorMessage = 'กำลังเข้าสู่ระบบอยู่ กรุณารอสักครู่'
        } else {
          this.errorMessage = `เข้าสู่ระบบไม่สำเร็จ: ${err.message || err.errorCode || 'Unknown error'}`
        }
      }
    },
  },
}
</script>

<style scoped>
.login-bg {
  min-height: 100vh;
  background-image: url('~@/assets/bg_sso_ic.jpg');
  background-size: cover;
  background-position: center;
}

.login-container {
  max-width: 570px;
}

.login-heading {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
}

.login-footer {
  text-align: center;
  margin-top: 16px;
  font-size: 0.85rem;
  color: #4b5563;
}

.login-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 24px 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(8px);
  text-align: center;
}


</style>